import { useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { MealType } from '@fytness/shared';
import { Text } from '@/components/ui/text';
import { Icon } from '~/global/components/icon/Icon';

/** Slot order the breakdown is rendered in, matching `MealTypeSelector`. */
const SLOT_ORDER: MealType[] = [
  MealType.BREAKFAST,
  MealType.LUNCH,
  MealType.SNACK,
  MealType.DINNER,
];

export interface MealLogSelectionBarProps {
  /** Items selected across the **whole day**, not just the visible slot (DD1). */
  totalCount: number;
  /** Per-slot counts, used for the breakdown line. */
  perSlotCounts: Partial<Record<MealType, number>>;
  /** Add every log in the *visible* slot to the selection (never replaces it). */
  onSelectAllVisible: () => void;
  onCopy: () => void;
  onMove: () => void;
  onCancel: () => void;
}

/**
 * The bottom bar shown while a day-scoped selection is being assembled.
 *
 * It reports the **cross-slot total**, plus a per-slot breakdown as soon as the
 * selection spans more than one slot — without it, items picked in a slot the
 * user has since navigated away from would be invisible, and the count would
 * read as a bug rather than as the feature (DD1).
 */
export const MealLogSelectionBar = ({
  totalCount,
  perSlotCounts,
  onSelectAllVisible,
  onCopy,
  onMove,
  onCancel,
}: MealLogSelectionBarProps) => {
  const { t } = useTranslation('meal');

  const hasSelection = totalCount > 0;

  const breakdown = useMemo(() => {
    const parts = SLOT_ORDER.filter((slot) => (perSlotCounts[slot] ?? 0) > 0);
    // One slot needs no breakdown: the total already says everything.
    if (parts.length < 2) return null;
    return parts
      .map((slot) =>
        t('copy.selection.breakdownItem', {
          count: perSlotCounts[slot] ?? 0,
          slot: t(`mealType.${slot.toLowerCase()}`, slot),
        }),
      )
      .join(t('copy.selection.breakdownSeparator'));
  }, [perSlotCounts, t]);

  const subtitle = hasSelection ? breakdown : t('copy.selection.emptyHint');

  return (
    <View
      testID="meal-selection-bar"
      collapsable={false}
      className="gap-3 rounded-2xl bg-surface p-4"
    >
      <View className="flex flex-row items-center gap-3">
        <View className="flex-1 gap-0.5">
          <Text
            testID="meal-selection-count"
            className="text-sm font-inter-semibold text-text-primary"
          >
            {hasSelection
              ? t('copy.selection.title', { count: totalCount })
              : t('copy.selection.empty')}
          </Text>
          {subtitle ? (
            <Text
              testID="meal-selection-breakdown"
              className="text-xs font-inter-regular text-text-muted"
            >
              {subtitle}
            </Text>
          ) : null}
        </View>

        <Pressable
          testID="meal-selection-select-all"
          collapsable={false}
          onPress={onSelectAllVisible}
          className="rounded-lg bg-surface-elevated px-3 py-2 active:opacity-70"
        >
          <Text className="text-xs font-inter-medium text-text-secondary">
            {t('copy.selection.selectAll')}
          </Text>
        </Pressable>

        <Pressable
          testID="meal-selection-cancel"
          collapsable={false}
          accessibilityLabel={t('copy.selection.cancel')}
          onPress={onCancel}
          hitSlop={8}
          className="h-8 w-8 items-center justify-center rounded-lg active:opacity-70"
        >
          <Icon name="X" size={16} className="text-text-muted" />
        </Pressable>
      </View>

      <View className="flex flex-row gap-3">
        <Pressable
          testID="meal-selection-copy"
          collapsable={false}
          disabled={!hasSelection}
          onPress={onCopy}
          className={`flex-1 flex-row items-center justify-center gap-2 rounded-xl px-4 py-3 active:opacity-70 ${
            hasSelection ? 'bg-accent' : 'bg-surface-elevated opacity-50'
          }`}
        >
          <Icon
            name="Copy"
            size={16}
            className={hasSelection ? 'text-background' : 'text-text-muted'}
          />
          <Text
            className={`text-sm font-inter-semibold ${
              hasSelection ? 'text-background' : 'text-text-muted'
            }`}
          >
            {t('copy.action')}
          </Text>
        </Pressable>

        <Pressable
          testID="meal-selection-move"
          collapsable={false}
          disabled={!hasSelection}
          onPress={onMove}
          className={`flex-1 flex-row items-center justify-center gap-2 rounded-xl bg-surface-elevated px-4 py-3 active:opacity-70 ${
            hasSelection ? '' : 'opacity-50'
          }`}
        >
          <Icon
            name="CornerUpRight"
            size={16}
            className="text-text-secondary"
          />
          <Text className="text-sm font-inter-semibold text-text-secondary">
            {t('copy.moveAction')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
};
